import { UserBlocker } from '@/hooks/use-user-blocker/types';
import {
    CircleCircle,
    CircleInvitation,
    CirclePartner,
    CirclePartnerConfig,
} from '@calm/evexia-web-sdk';
import { useAccessCode } from '@rhg/hooks/use-access-code';
import useAccount from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useDependentPartnerById } from '@rhg/hooks/use-dependent-partner';
import { useInvitationByCode } from '@rhg/hooks/use-invitation';
import { useInviteCode } from '@rhg/hooks/use-invite-code';
import { usePartnerFromAccessCode } from '@rhg/hooks/use-partner-from-access-code';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { DEFAULT_COUNTRY_CODE } from '@rhg/utils/constants';
import {
    getInvitationStatus,
    InvitationErrorType,
    InvitationStatus,
} from '@rhg/utils/invitations';
import { getIsSponsorRelatedSponsorship } from '@rhg/utils/partner';
import { routes } from '@rhg/utils/routes';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { InvitationErrorDialog } from '../../invitation-error-dialog';
import { SkipLinkingSSOQueryKey } from '../../linking-sso-step/types';

const RegistrationDialog = dynamic(() =>
    import('../../dialog').then((mod) => mod.RegistrationDialog)
);

const excludedPages = [
    routes.auth,
    routes.error,
    routes.expired,
    routes.notFound,
    routes.login,
    routes.standaloneScreening,
    routes.standaloneResults,
    routes.signup,
    routes.saml,
    routes.sso,
    routes.ssoInfo,
    routes.registration,
    routes.reconsent,
];

function getDefaultRegistrationFlow(
    isSponsored: boolean,
    isValidInvitation: boolean
) {
    if (isSponsored) {
        if (isValidInvitation) {
            return RegistrationFlow.ChangeOfInviteeSponsorship;
        } else {
            return RegistrationFlow.ChangeOfSponsorship;
        }
    } else {
        if (isValidInvitation) {
            return RegistrationFlow.ExistingInviteeRegistration;
        } else {
            return RegistrationFlow.ExistingRegistration;
        }
    }
}

function getDefaultRegistrationStep(
    isValidInvitation: boolean,
    hasAccessCode: boolean,
    isSponsored: boolean
) {
    if (isValidInvitation) {
        return RegistrationFlowStep.ReceivingInvitation;
    } else if (!hasAccessCode) {
        return RegistrationFlowStep.AccessCode;
    } else if (isSponsored) {
        return RegistrationFlowStep.ChangeSponsorship;
    } else {
        return RegistrationFlowStep.Eligibility;
    }
}

enum RegistrationDialogBlocker {
    Blocked = 'Blocked',
    ExcludedPage = 'ExcludedPage',
    NotReady = 'NotReady',
    NotOpen = 'NotOpen',
    Invalid = 'Invalid',
    InvitationExpired = 'InvitationExpired',
    InvitationClaimed = 'InvitationClaimed',
    InvitationCountry = 'InvitationCountry',
    Related = 'Related',
}

function getRegistrationDialogBlocker({
    isAccountFetched,
    isInviteCodeReady,
    isInviteCodeFromQuery,
    isInvitationFetched,
    isInvitationPartnerFetched,
    invitation,
    invitationError,
    invitationPartnerError,
    isUserBlockerLoading,
    isSkippingSSOLinking,
    userBlocker,
    isRouteReady,
    isAccessCodeReady,
    isActiveCircleLoading,
    isAccessCodeFromQuery,
    isAccessCodePartnerFetched,
    isRegistrationDialogOpen,
    pathname,
    activeCircle,
    brandingPartnerConfig,
}: {
    isAccountFetched: boolean;
    isInviteCodeReady: boolean;
    isInviteCodeFromQuery: boolean;
    isInvitationFetched: boolean;
    isInvitationPartnerFetched: boolean;
    isUserBlockerLoading: boolean;
    isSkippingSSOLinking: boolean;
    invitation?: CircleInvitation;
    invitationError?: InvitationErrorType;
    invitationPartner?: CirclePartner;
    invitationPartnerError?: InvitationErrorType;
    userBlocker?: UserBlocker;
    isRouteReady: boolean;
    isAccessCodeReady: boolean;
    isActiveCircleLoading: boolean;
    isAccessCodeFromQuery: boolean;
    isAccessCodePartnerFetched: boolean;
    isRegistrationDialogOpen: boolean;
    pathname: string;
    activeCircle?: CircleCircle;
    brandingPartnerConfig?: CirclePartnerConfig;
}): RegistrationDialogBlocker | null {
    const isReady =
        !isUserBlockerLoading &&
        isAccountFetched &&
        isRouteReady &&
        isAccessCodeReady &&
        !isActiveCircleLoading &&
        (!isAccessCodeFromQuery || isAccessCodePartnerFetched) &&
        isInviteCodeReady &&
        (!isInviteCodeFromQuery ||
            (isInvitationFetched &&
                (!invitation || isInvitationPartnerFetched)));

    if (!isRegistrationDialogOpen) {
        return RegistrationDialogBlocker.NotOpen;
    }

    if (excludedPages.includes(pathname)) {
        return RegistrationDialogBlocker.ExcludedPage;
    }

    // If the dependencies are not ready or if it is not supposed to be shown
    if (!isReady) {
        return RegistrationDialogBlocker.NotReady;
    }

    if (
        userBlocker &&
        [
            UserBlocker.CountryUnsponsored,
            UserBlocker.Expired,
            UserBlocker.CCHDUnsponsored,
            ...(isSkippingSSOLinking ? [] : [UserBlocker.LinkingSSO]),
            UserBlocker.Tos,
            UserBlocker.ReceivingInvitation,
            UserBlocker.Reconsent,
        ].includes(userBlocker)
    ) {
        return RegistrationDialogBlocker.Blocked;
    }

    // If the access code is present but invalid
    if (
        isAccessCodeFromQuery &&
        isAccessCodePartnerFetched &&
        !brandingPartnerConfig
    ) {
        return RegistrationDialogBlocker.Invalid;
    }

    // If the access code is from a related sponsorship
    if (
        isAccessCodeFromQuery &&
        isAccessCodePartnerFetched &&
        brandingPartnerConfig &&
        getIsSponsorRelatedSponsorship(
            activeCircle?.sponsor,
            brandingPartnerConfig?.id
        )
    ) {
        return RegistrationDialogBlocker.Related;
    }

    // If the invite is present but invalid/expired
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        (!invitation ||
            [InvitationStatus.Expired, InvitationStatus.Deleted].includes(
                getInvitationStatus(invitation)
            ))
    ) {
        return RegistrationDialogBlocker.InvitationExpired;
    }

    // If the invite is from a different country than the user's current country
    if (
        isInviteCodeFromQuery &&
        ((isInvitationFetched && invitationError === 'country') ||
            (isInvitationPartnerFetched &&
                invitationPartnerError === 'country'))
    ) {
        return RegistrationDialogBlocker.InvitationCountry;
    }

    // If the invite is present but from the same user
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        invitation?.senderRecipientId &&
        invitation?.senderRecipientId === activeCircle?.recipient?.id
    ) {
        return RegistrationDialogBlocker.Related;
    }

    // If the invite is present but claimed
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        invitation &&
        getInvitationStatus(invitation) === 'claimed'
    ) {
        return RegistrationDialogBlocker.InvitationClaimed;
    }

    return null;
}

export const RegistrationDialogManager = () => {
    const { pathname, query, isReady: isRouteReady } = useRouter();
    const { account, isFetched: isAccountFetched } = useAccount();
    const { activeCircle, isLoading: isActiveCircleLoading } =
        useActiveCircle();
    const { isLoading: isUserBlockerLoading, userBlocker } = useUserBlocker();
    const {
        isInviteCodeFromQuery,
        inviteCode,
        isReady: isInviteCodeReady,
    } = useInviteCode();
    const {
        invitation,
        invitationError,
        isFetched: isInvitationFetched,
    } = useInvitationByCode(inviteCode);

    const {
        dependentPartner: invitationPartner,
        dependentPartnerError: invitationPartnerError,
        isFetched: isInvitationPartnerFetched,
    } = useDependentPartnerById({
        dependentPartnerId: invitation?.attributes?.partnerId,
        countryCode: account?.countryCode ?? DEFAULT_COUNTRY_CODE,
    });
    const { isAccessCodeFromQuery, isReady: isAccessCodeReady } =
        useAccessCode();
    const { brandingPartnerConfig, isFetched: isAccessCodePartnerFetched } =
        usePartnerFromAccessCode();

    const isSkippingSSOLinking = Boolean(query[SkipLinkingSSOQueryKey]);
    const {
        closeRegistrationDialog,
        isRegistrationDialogOpen,
        registrationDialogFlow,
        registrationDialogStep,
    } = useRegistrationDialog();

    const [isDialogForceRendered, setIsDialogForceRendered] = useState(false);
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(true);

    const registrationDialogBlocker = getRegistrationDialogBlocker({
        isAccountFetched,
        isInviteCodeReady,
        isInviteCodeFromQuery,
        isInvitationFetched,
        isInvitationPartnerFetched,
        invitation: invitation ?? undefined,
        invitationError: invitationError ?? undefined,
        invitationPartner: invitationPartner ?? undefined,
        invitationPartnerError: invitationPartnerError ?? undefined,
        isUserBlockerLoading,
        isSkippingSSOLinking,
        userBlocker,
        isRouteReady,
        isAccessCodeReady,
        isActiveCircleLoading,
        isAccessCodeFromQuery,
        isAccessCodePartnerFetched,
        isRegistrationDialogOpen,
        pathname,
        activeCircle,
        brandingPartnerConfig,
    });

    // If we have already opened the dialog (meaning there is no blocker), we should keep showing the dialog
    useEffect(() => {
        if (!registrationDialogBlocker) {
            setIsDialogForceRendered(true);
        }
    }, [registrationDialogBlocker]);

    // We should not force the dialog to render if the blocker is set to ExcludedPage or if the dialog has been closed (this can happen if the user is force navigated to registration)
    useEffect(() => {
        if (
            registrationDialogBlocker ===
                RegistrationDialogBlocker.ExcludedPage ||
            !isRegistrationDialogOpen
        ) {
            setIsDialogForceRendered(false);
        }
    }, [isRegistrationDialogOpen, registrationDialogBlocker]);

    if (
        registrationDialogBlocker === RegistrationDialogBlocker.ExcludedPage ||
        (registrationDialogBlocker === RegistrationDialogBlocker.NotReady &&
            !isDialogForceRendered)
    ) {
        return null;
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationExpired &&
        isErrorDialogOpen
    ) {
        return (
            <InvitationErrorDialog
                type="expired"
                onClose={() => setIsErrorDialogOpen(false)}
            />
        );
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationCountry &&
        isErrorDialogOpen
    ) {
        return (
            <InvitationErrorDialog
                type="country"
                onClose={() => setIsErrorDialogOpen(false)}
            />
        );
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationClaimed &&
        isErrorDialogOpen
    ) {
        return (
            <InvitationErrorDialog
                type="claimed"
                onClose={() => setIsErrorDialogOpen(false)}
            />
        );
    }

    const defaultRegistrationFlow = getDefaultRegistrationFlow(
        Boolean(activeCircle?.sponsor),
        Boolean(invitation && invitationPartner)
    );
    const defaultRegistrationStep = getDefaultRegistrationStep(
        Boolean(invitation && invitationPartner),
        isAccessCodeFromQuery,
        Boolean(activeCircle?.sponsor)
    );

    if (!registrationDialogBlocker || isDialogForceRendered) {
        return (
            <RegistrationDialog
                closeDialog={closeRegistrationDialog}
                registrationFlow={
                    registrationDialogFlow || defaultRegistrationFlow
                }
                currentStep={registrationDialogStep || defaultRegistrationStep}
            />
        );
    }
};
